import { useState } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { IconButton } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { CONTEXTUAL_ENTITY_ICONS } from "src/unifiedCopilot/context/entityDefinitions";
import { type ContextualEntity } from "src/unifiedCopilot/context/types";

type Props = {
  entity: Pick<ContextualEntity, "displayName" | "type">;
  dataLoc?: string;
  maxWidth?: boolean;
  /** Renders the chip as a button, e.g. to navigate to the resource. */
  onClick?: () => void;
  /**
   * Renders a trailing × that drops the entity from the automatic context.
   * Composer-only: not meant to be combined with `onClick`.
   */
  onRemove?: () => void;
};

/**
 * Inline-safe representation of a contextual entity. It can be rendered
 * inside editor/message text as well as in the composer's chip row.
 */
export const ContextualEntityChip: React.FC<Props> = ({
  entity,
  dataLoc,
  maxWidth = false,
  onClick,
  onRemove,
}) => {
  // The design-system Pill has no hover style of its own; a clickable chip
  // drives the Pill's controlled `hovered` state from its button wrapper.
  const [isHovered, setIsHovered] = useState(false);

  const pill = (
    <Pill
      as="span"
      dataLoc={dataLoc}
      hovered={isHovered}
      maxWidth={maxWidth}
      variant="outlined-white"
    >
      <Pill.Icon icon={CONTEXTUAL_ENTITY_ICONS[entity.type]} />
      <Pill.Text>{entity.displayName}</Pill.Text>
      {onRemove && (
        <IconButton
          aria-label={`Remove ${entity.displayName} from context`}
          icon={faXmark}
          size="2xs"
          onClick={onRemove}
        />
      )}
    </Pill>
  );

  if (!onClick) {
    return pill;
  }

  return (
    <button
      className="max-w-full rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500/25"
      type="button"
      onBlur={() => setIsHovered(false)}
      onClick={onClick}
      onFocus={() => setIsHovered(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {pill}
    </button>
  );
};
